<script lang="ts">
	import { Input, type InputProps } from "flowbite-svelte-v1";
	import { twJoin, twMerge } from "tailwind-merge";
	import DateRangePicker from "#lib/components/DateRangePicker.svelte";
	import Icon from "#lib/components/Icon.svelte";
	import Label from "#lib/components/Label.svelte";
	import TimeInput from "#lib/components/calendar/TimeInput.svelte";
	import { formatDate } from "#lib/timestamp.js";

	interface Props {
		timestamp?: Date;
		allDay?: boolean;
		dateLabel?: string;
		datePlaceholder?: string;
		dateFormatOptions?: Intl.DateTimeFormatOptions;
		timeLabel?: string;
		timezone?: string;
		color?: InputProps["color"];
		disabled?: boolean;
		required?: boolean;
		class?: string;
		onChange?: (date: Date) => void;
	}

	let { timestamp = $bindable(), allDay = false, dateLabel, datePlaceholder, dateFormatOptions, timeLabel, timezone, color = "default", disabled, required, onChange, class: className }: Props = $props();

	const time = $derived(timestamp ? `${timestamp.getHours().toString().padStart(2, "0")}:${timestamp.getMinutes().toString().padStart(2, "0")}` : undefined);

	let isOpen = $state(false);

	function handleDateChange(event: CustomEvent<{ startDate: number; endDate?: number }>) {
		const { startDate } = event.detail;
		const date = new Date(startDate);
		const updated = new Date(date.getFullYear(), date.getMonth(), date.getDate(), timestamp?.getHours() ?? 0, timestamp?.getMinutes() ?? 0);
		onChange?.(updated);
	}

	function handleTimeChange(data: { time: string; endTime: string; [key: string]: string }) {
		const [hours, minutes] = data.time.split(":").map(Number);
		const updated = new Date(timestamp?.getFullYear() ?? 0, timestamp?.getMonth() ?? 0, timestamp?.getDate() ?? 0, hours, minutes);
		onChange?.(updated);
	}
</script>

<div class={twMerge("flex w-full gap-2", className)}>
	<div class="relative flex grow flex-col gap-2">
		{#if dateLabel}
			<Label class={twJoin("block text-sm font-medium text-gray-700", required ? "required" : "")}>{dateLabel}</Label>
		{/if}
		<DateRangePicker bind:isOpen startDate={timestamp?.getTime() ?? new Date().getTime()} isRange={false} enableFutureDates enablePastDates on:change={handleDateChange}>
			<Input class="cursor-pointer" size="md" {color} {disabled} type="text" value={timestamp ? formatDate(timestamp, timezone, dateFormatOptions) : ""} placeholder={datePlaceholder} data-action-name="calendar-date-input" onclick={() => (isOpen = true)} readonly>
				{#snippet right()}
					<Icon iconSet="heroicons" name="calendar-days" class="h-5 w-5 text-gray-500" />
				{/snippet}
			</Input>
		</DateRangePicker>
	</div>
	{#if !allDay}
		<div class="flex flex-none flex-col gap-2">
			{#if timeLabel}
				<Label class={twJoin("block text-sm font-medium text-gray-700", required ? "required" : "")}>{timeLabel}</Label>
			{/if}
			<TimeInput value={time} divClass="shadow-none" inputColor={color} {disabled} onselect={handleTimeChange} />
		</div>
	{/if}
</div>
